import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCellarStore } from '../store/CellarStoreContext';
import {
  deletePurchase,
  deleteTasting,
  deleteWine,
  drinkBottle,
  editPurchase,
  editTasting,
  editWine,
  recordPurchase,
  removeLabelPhoto,
  setLabelPhoto,
  setOnlineRating,
  wineStock,
  WineIdentityConflictError,
  type Purchase,
  type Tasting,
  type Wine,
} from '../domain/cellar';
import { compareMonths } from '../domain/months';
import { DrinkForm, type DrinkSubmission } from '../components/DrinkForm';
import { TastingHistoryList } from '../components/TastingHistoryList';
import { DeleteConfirmation } from '../components/DeleteConfirmation';
import { LabelPhotosSection } from '../components/LabelPhotosSection';
import { OnlineRatingControl } from '../components/OnlineRatingControl';
import { OnlineRatingValue } from '../components/OnlineRatingValue';
import { grapeChoices } from '../domain/grapes';
import { onlineRatingNotes, onlineRatingToShow } from '../domain/online-rating';
import { latestRatedTasting } from '../domain/rating';
import { useBackend } from '../backend/BackendContext';
import { bottleCount, onlineRatingNote, t, tf } from '../i18n';
import { userMessage } from '../i18n/errors';
import {
  AddPurchaseForm,
  PurchaseRecord,
  TastingEditForm,
  WineFieldsSection,
  type PurchaseChange,
  type TastingChange,
} from './WineDetailSections';
import './WineDetailPage.css';

const SAVE_FAILED_MESSAGE = t('error.saveFailed');

/**
 * /c/:cellarId/vin/:wineId: identity, stock, a drink action (the same confirm form as the card),
 * every field of the wine, its purchases and its tastings editable in place, and deleting any of
 * them after a confirmation (User Story 7, T118, FR-029; since 2026-10-09 one "Modifier" per
 * section or record turns it into a form, WineDetailSections.tsx) and the tasting history (User
 * Story 5). Months read MM/YY (owner, 2026-09-28). Owner, 2026-10-15: the bottles left in brackets
 * after the name; "Boire une bouteille", "Ajouter une bouteille" (a purchase of this wine) and
 * "Note en ligne" side by side, the same size; no rating chart; an unrated tasting reads "Pas de
 * note" and is rated with its "Modifier".
 */
export function WineDetailPage() {
  const { cellarId, wineId } = useParams<{ cellarId: string; wineId: string }>();
  const navigate = useNavigate();
  const { assist, photos } = useBackend();
  const { cellar, apply } = useCellarStore();
  const [error, setError] = useState<string | undefined>();
  const [drinking, setDrinking] = useState(false);
  const [adding, setAdding] = useState(false);

  if (!cellar) {
    return (
      <main>
        <p>{t('common.loading')}</p>
      </main>
    );
  }

  const found = cellar.wines.find((w) => w.id === wineId);
  if (!found) {
    return (
      <main>
        <p>{t('notFound.body')}</p>
      </main>
    );
  }
  const wine: Wine = found;
  const stock = wineStock(wine);

  function handleDrink(value: DrinkSubmission) {
    setError(undefined);
    setDrinking(false);
    try {
      apply(drinkBottle({ wineId: wine.id, ...value })).catch(() => setError(SAVE_FAILED_MESSAGE));
    } catch {
      setError(SAVE_FAILED_MESSAGE);
    }
  }

  /** A duplicate identity names the other wine. */
  const wineError = (failure: unknown) => {
    if (failure instanceof WineIdentityConflictError) {
      const other = cellar!.wines.find((w) => w.id === failure.conflictingWineId);
      if (other) {
        return tf('error.wineDuplicateNamed', {
          wine: [other.domaine, other.cepage, other.parcelle, String(other.annee)]
            .filter(Boolean)
            .join(' · '),
        });
      }
    }
    return userMessage(failure);
  };

  const savePurchase = (purchase: Purchase, change: PurchaseChange) =>
    apply(
      editPurchase({
        wineId: wine.id,
        purchaseId: purchase.id,
        expectedVersion: wine.version,
        ...change,
      }),
    );

  const saveTasting = (tasting: Tasting, change: TastingChange) =>
    apply(
      editTasting({
        wineId: wine.id,
        tastingId: tasting.id,
        expectedVersion: wine.version,
        tastedOn: change.tastedOn,
        ...(change.rating !== null ? { rating: change.rating } : {}),
        ...(change.notes.trim() !== '' ? { notes: change.notes } : {}),
      }),
    );

  const onlineRating = onlineRatingToShow(wine);
  const grapes = grapeChoices(cellar?.wines ?? []);
  const notes = onlineRatingNotes(wine);
  const purchases = [...wine.purchases].sort(
    (a, b) => compareMonths(a.purchasedOn, b.purchasedOn) || a.createdAt - b.createdAt,
  );

  // "Note en ligne", saved apart from the person's ratings and gone once they rate the wine
  // (owner, 2026-10-03).
  const unrated = !latestRatedTasting(wine.tastings);
  const actions = (stock > 0 ? 1 : 0) + 1 + (unrated && assist ? 1 : 0);

  return (
    <main className="wine-detail">
      <h1>
        {wine.domaine} {wine.annee}{' '}
        <span className="wine-detail__stock">({bottleCount(stock)})</span>
      </h1>

      {error && <p role="alert">{error}</p>}

      <div
        className="wine-detail__top"
        style={{ gridTemplateColumns: `repeat(${actions}, minmax(0, 1fr))` }}
      >
        {stock > 0 && (
          <button
            type="button"
            className="cta-action"
            aria-expanded={drinking}
            onClick={() => {
              setAdding(false);
              setDrinking(true);
            }}
          >
            {t('drink.action')}
          </button>
        )}
        <button
          type="button"
          className="cta-action"
          aria-expanded={adding}
          onClick={() => {
            setDrinking(false);
            setAdding(true);
          }}
        >
          {t('inventory.addPurchase')}
        </button>
        {unrated && assist && (
          <OnlineRatingControl
            client={assist}
            showResult={false}
            onFound={(saved) => {
              try {
                apply(setOnlineRating({ wineId: wine.id, onlineRating: saved })).catch(() =>
                  setError(SAVE_FAILED_MESSAGE),
                );
              } catch {
                setError(SAVE_FAILED_MESSAGE);
              }
            }}
            wine={{
              domaine: wine.domaine,
              cepage: wine.cepage,
              parcelle: wine.parcelle ?? '',
              annee: String(wine.annee),
              typeDeVin: wine.typeDeVin,
            }}
          />
        )}
      </div>
      {unrated && onlineRating && (
        <p className="online-rating__found">
          <OnlineRatingValue saved={onlineRating} />
        </p>
      )}

      {drinking && (
        <DrinkForm id="drink" onConfirm={handleDrink} onCancel={() => setDrinking(false)} />
      )}
      {adding && (
        <AddPurchaseForm
          onClose={() => setAdding(false)}
          onSave={(purchase) =>
            apply(
              recordPurchase({
                // The wine's own identity: the purchase is added to it (FR-003).
                wine: {
                  domaine: wine.domaine,
                  cepage: wine.cepage,
                  ...(wine.parcelle !== undefined ? { parcelle: wine.parcelle } : {}),
                  annee: wine.annee,
                  pays: wine.pays,
                  ...(wine.region !== undefined ? { region: wine.region } : {}),
                  typeDeVin: wine.typeDeVin,
                },
                ...purchase,
              }),
            )
          }
        />
      )}

      <LabelPhotosSection
        cellarId={cellarId!}
        wine={wine}
        photos={photos}
        onTaken={(side, photoId) => apply(setLabelPhoto({ wineId: wine.id, side, photoId }))}
        onRemoved={(side) => apply(removeLabelPhoto({ wineId: wine.id, side }))}
      />

      <WineFieldsSection
        wine={wine}
        stock={stock}
        grapes={grapes}
        errorMessage={wineError}
        onSave={(details) =>
          apply(editWine({ wineId: wine.id, expectedVersion: wine.version, details }))
        }
      />

      <section className="wine-detail__section" aria-labelledby="wine-purchases">
        <h2 id="wine-purchases">{t('purchase.history')}</h2>
        <ol className="wine-detail__records">
          {purchases.map((purchase) => (
            <PurchaseRecord
              key={purchase.id}
              purchase={purchase}
              onSave={(change) => savePurchase(purchase, change)}
              onDelete={() =>
                apply(
                  deletePurchase({
                    wineId: wine.id,
                    purchaseId: purchase.id,
                    expectedVersion: wine.version,
                  }),
                )
              }
            />
          ))}
        </ol>
      </section>

      <section className="wine-detail__section" aria-labelledby="wine-tastings">
        <h2 id="wine-tastings">{t('tasting.history')}</h2>
        <TastingHistoryList
          tastings={wine.tastings}
          renderEdit={(tasting, close) => (
            <TastingEditForm
              tasting={tasting}
              onClose={close}
              onSave={(change) => saveTasting(tasting, change)}
              onDelete={() =>
                apply(
                  deleteTasting({
                    wineId: wine.id,
                    tastingId: tasting.id,
                    expectedVersion: wine.version,
                  }),
                )
              }
            />
          )}
        />
      </section>

      {/* The sites' ratings of the last "Note en ligne", dated (owner, 2026-10-03). */}
      {notes.length > 0 && (
        <section className="wine-detail__section" aria-labelledby="wine-notes">
          <h2 id="wine-notes">{t('rating.onlineNotes')}</h2>
          <ul className="wine-notes">
            {notes.map((note) => (
              <li key={`${note.site}-${note.scale}`}>{onlineRatingNote(note)}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="wine-detail__section">
        <DeleteConfirmation
          label={t('wine.delete')}
          warning={t('wine.deleteWarning')}
          onConfirm={() => {
            // apply() refuses synchronously (staying here) if the wine changed meanwhile; once
            // deleted there is nothing left to show, so go back to the cellar.
            const saving = apply(deleteWine({ wineId: wine.id, expectedVersion: wine.version }));
            navigate(`/c/${cellarId}/stock`);
            return saving;
          }}
        />
      </section>
    </main>
  );
}
